import { useInjection } from "inversify-react";
import ApiService from "../../services/apiService";
import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { ApplicationUserApi, ApplicationUserDto, DepartmentApi, PeopleDetailsDto } from "../../api";
import { loadingSpin, RoutingPaths } from "../routing/routingContainer";
import GweLayout from "../../components/gweLayout/gweLayout";
import { Button, Col, Divider, List, Result, Space, Spin, Tag, Typography } from "antd";
import { UserTags } from "../../components/userTile/userTile";
import { Link } from "react-router-dom";
import styles from "./people.module.less";
import ProfilePicture from "../../components/profilePicture/profilePicture";
import DepartmentSelector from "../../components/departmentSelect/departmentSelect";
import { useDispatch, useSelector } from "react-redux";
import { getDepartments, setDepartments } from "../../redux/productSlice";
import heroImage from "../../images/unlimited-consults.png";
import phoneIcon from "../../images/phone-icon.png";
import heroStyles from "./hero.module.less";
import { ReactComponent as LogoFullSvg } from "../../logo-full-white.svg";
import NewCaseStickyButton from "../../components/newCaseStickyButton/newCaseStickyButton";
import Title from "antd/lib/typography/Title";
import Paragraph from "antd/lib/typography/Paragraph";

const { Text } = Typography;

export default function UnlimitedConsults(props: { editable?: boolean }) {
    const apiService = useInjection(ApiService);
    const dispatch = useDispatch();

    const [ users, setUsers ] = useState<ApplicationUserDto[] | null>();

    useEffect(() => {
        const updateDepartments = async () => {
            const response = await apiService.getApi(DepartmentApi).apiDepartmentGet();

            dispatch(setDepartments(response.data));
        }

        updateDepartments();
    }, [apiService]);

    const getExpertEmployees = useCallback(async () => {
        return await apiService.getApi(ApplicationUserApi).apiApplicationUserManagersGet();
    }, [ apiService ]);

    const refreshUserList = useCallback(async () => {
        try {
            const response = await getExpertEmployees();
            setUsers(response.data);
        } catch (e: any) {
            console.log(e.response?.data);
        }
    }, [ getExpertEmployees ]);

    useEffect(() => {
        refreshUserList();
    }, [ refreshUserList ]);

    const filtered = props.editable ? users : users?.filter(u => u.departments?.length);

    if (!!filtered && filtered.length === 0) {
        return <GweLayout title="Nielimitowane konsultacje">
            <>
                <br/>
                <Result
                    title="Wygląda na to że nie masz przydzielonego żadnego managera."
                    extra={
                        <Link to={RoutingPaths.mainPage.route}>
                            <Button type="primary" key="console">
                                Powrót
                            </Button>
                        </Link>
                    }
                />
            </>
        </GweLayout>;
    }

    return <GweLayout
        title="Nielimitowane konsultacje"
        subtitle={users ? `(${users?.length} pozycji)` : undefined}
    >
        <Col xs={22} md={20} lg={18} xxl={16}>
            <HeroWithSubscriptionCta title="Nielimitowane konsultacje" sellingPoints={["Konsultacje bez limitu", "Eksperci zawsze pod ręką", "Doradztwo w abonamencie"]} img={heroImage} />
            <ContactTile
                title="Twoi eksperci zawsze pod ręką"
                description={[
                    <>Koniec z obawami o koszty każdej rozmowy z doradcą! W ramach abonamentu Goodwill Experts oferujemy Ci <b>prawdziwie nielimitowane konsultacje</b> z ekspertami z ponad 20 dziedzin – od księgowości i prawa, przez marketing i e-commerce, aż po energię i ESG.</>,
                    <>Masz bieżące pytanie podatkowe? Potrzebujesz porady dotyczącej rekrutacji? Chcesz skonsultować strategię PR?</>,
                    <>Po prostu zadzwoń lub napisz! Nasi specjaliści są dostępni, aby wspierać rozwój Twojej firmy na każdym etapie, a Ty możesz korzystać z ich wiedzy bez limitu.</>
                ]}
                cta={[
                    { name: "Skorzystaj z nielimitowanego doradzctwa:", value: <a href="tel:+48608162567">608 162 567</a> },
                    { name: "Potrzebujesz porady w konkretnej dziedzinie?", value: <Link to={RoutingPaths.yourCaseAdd.route}>Zgłoś nową sprawę</Link> },
                    { name: "Napisz do nas:", value: <a href="mailto:konsultacje@gwexperts.pl">konsultacje@gwexperts.pl</a> }
                ]}
            />
            <NewCaseStickyButton />
        </Col>
    </GweLayout>
}

export function HeroWithSubscriptionCta(props: { title: string, sellingPoints: string[], img: string }) {
    return <div className={heroStyles.container}>
        <img className={heroStyles.image} src={props.img} alt="Hero" />
        <div className={heroStyles.mainContent}>
            <h1>{props.title}</h1>
            <a href="tel:+48608162567" className={heroStyles.phoneCta}>
                <img src={phoneIcon} alt="" />
                <span className={heroStyles.number}>608 162 567</span>
            </a>
            <div className={heroStyles.callContainer}>
                <div className={heroStyles.call}>Zadzwoń</div>
                <div className={heroStyles.orAddCase}>lub kliknij “<Link to="/yourCases/add">zgłoś sprawę</Link>”</div>
            </div>
        </div>
        <div className={heroStyles.rightColumn}>
            <LogoFullSvg />
            <div className={heroStyles.sellingPoints}>
                {props.sellingPoints.map((point, i) =>
                <div key={i} className={heroStyles.sellingPoint}>
                    {point}
                </div>)}
            </div>
        </div>
    </div>;
}

export function ContactTile(props: { title: string, description: ReactNode[], cta: { name: string, value: ReactNode }[] }) {
    return <Space direction="vertical" style={{ width: "100%" }}>
        <br />
        <Title>{props.title}</Title>
        <Paragraph style={{ whiteSpace: "pre-line" }}>
            {props.description.map((item, i) => <span key={i}>{item}<br/></span>)}
        </Paragraph>
        {props.cta.map((item, i) =>
            <Space key={i}>
                <Text type="secondary">{item.name}</Text>
                {item.value}
            </Space>)}
        <Divider />
    </Space>
}

function ManagersList({filtered, editable} : { filtered?: ApplicationUserDto[], editable?: boolean }) {
    return <>
        <List
            grid={{ gutter: 24, column: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
            dataSource={filtered}
            renderItem={user => <List.Item key={user.id}>
                <ManagerTile user={user} editable={editable} />
            </List.Item>}
        />
    </>
}

function ManagerTile({user, editable}: { user: ApplicationUserDto, editable?: boolean }) {

    return <div
        className={styles.tileContainer}>
        <div className={styles.userInfoWrapper}>
            <div className={styles.profileWrapper}>
                <ProfilePicture className={styles.userProfilePicture}
                                profilePictureId={user.profileImageId} />
                <UserTags user={user} />
            </div>
            <div className={styles.rightColumn}>
                <div className={styles.userInfo}>
                    <div className={styles.userName}>{user.firstName} {user.lastName}</div>
                    <em>{(user.managerTitle?.name || "Manager")}</em>
                </div>
                <div className={styles.userInfo}>
                    <ManagerDepartments user={user} editable={editable} />
                </div>
            </div>
        </div>
    </div>
}

export function ManagerContactTile({user}: { user: ApplicationUserDto }) {

    return <div>
        <div className={styles.userInfoWrapper}>
            <div className={styles.profileWrapper}>
                <ProfilePicture className={styles.userProfilePicture}
                                profilePictureId={user.profileImageId} />
                <UserTags user={user} />
            </div>
            <div className={styles.rightColumn}>
                <div className={styles.userInfo}>
                    <div className={styles.userName}>{user.firstName} {user.lastName}</div>
                    <em>{(user.managerTitle?.name || "Manager")}</em>
                    <div className={styles.userInfoItem}>{user.phoneNumber}</div>
                    <div className={styles.userInfoItem}>{user.email}</div>
                    <div className={styles.userInfoItem}>{user.profession?.name}</div>
                </div>
            </div>
        </div>
    </div>
}

function ManagerDepartments({user, editable} : { user: ApplicationUserDto, editable?: boolean }) {

    const apiService = useInjection(ApiService);
    const [isLoading, setIsLoading] = useState(false);
    const [departments, setDepartments] = useState(user.departments);
    const allDepartments = useSelector(getDepartments);

    const updateDepartments = async (ids: number[]) => {
        setIsLoading(true);

        try {
            const response = await apiService.getApi(ApplicationUserApi).apiApplicationUserDepartmentsIdPost(user.id!, ids);

            setDepartments(response.data.departments)
        } finally {
            setIsLoading(false);
        }
    }

    const onChangeOnPosition = (value: number, key: number) => {
        let updated = [...(departments || [])];

        updated[key] = value;

        updateDepartments(updated.filter(u => u));
    }

    const onAddedNew = (value: number) => {
        updateDepartments([...(departments || []), value]);
    }

    return <Spin spinning={isLoading}>
        <div className={styles.departments}>
            <Text type="secondary">Obszary specjalizacji managera:</Text>
            <div className={styles.departmentList}>
                {editable && departments?.map((d, i) =>
                    <DepartmentSelector key={i} value={d} onChange={v => onChangeOnPosition(v, i)} />)}
                {editable && <DepartmentSelector value={null} onChange={onAddedNew} key={"new"} />}
                {!editable && departments?.map((id, i) => <Tag key={i}>{allDepartments?.find(d => d.id === id)?.name?.replace(/ *\([^)]*\) */g, "")}</Tag>)}
            </div>
        </div>
    </Spin>
}